import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    CircularProgress,
    Container,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchClients } from '../../store/slices/clientSlice';
import {
    clearFinanceLedger,
} from '../../store/slices/financeLedgerSlice';
import LedgerTab from './components/LedgerTab';
import PaymentsTab from './components/PaymentsTab';
import PaymentCreateTab from './components/PaymentCreateTab';
import NotesTab from './components/NotesTab';
import InvoicesTab from './components/InvoicesTab';

type FinanceTab = 'ledger' | 'payments' | 'newPayment' | 'notes' | 'invoices';

const pathToTabMap: Record<string, FinanceTab> = {
    '/customer-finances': 'ledger',
    '/ledger': 'ledger',
    '/payments': 'payments',
    '/new-payment': 'newPayment',
    '/debit-credit-notes': 'notes',
    '/invoices': 'invoices',
};

const tabToPathMap: Record<FinanceTab, string> = {
    'ledger': '/ledger',
    'payments': '/payments',
    'newPayment': '/new-payment',
    'notes': '/debit-credit-notes',
    'invoices': '/invoices',
};

const CustomerFinances: React.FC = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAppSelector((state) => state.auth);
    const { clients, loading: clientsLoading } = useAppSelector((state) => state.clients);

    const [activeTab, setActiveTab] = useState<FinanceTab>('ledger');
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined);
    const [customerOptions, setCustomerOptions] = useState<Array<{ id: number; name: string }>>([]);

    const isClient = user?.type === 'CLIENT';
    const isAdminOrManager = user?.type === 'ADMIN' || user?.type === 'MANAGER';

    // Determine if "All" option should be available for current tab
    const allowAllOption = activeTab === 'ledger' || activeTab === 'invoices';

    useEffect(() => {
        if (isAdminOrManager) {
            dispatch(fetchClients({ per_page: 1000 }));
        }
    }, [dispatch, isAdminOrManager]);

    useEffect(() => {
        if (clients) {
            const options = clients.map((c) => ({ id: c.id, name: `${c.name}${c.client_profile?.company_name ? ` (${c.client_profile.company_name})` : ''}` }));
            setCustomerOptions(options);
        }
    }, [clients, allowAllOption, isClient]);

    // Sync activeTab with URL
    useEffect(() => {
        const tab = pathToTabMap[location.pathname];
        if (tab) {
            setActiveTab(tab);
        }
    }, [location.pathname]);

    // If user is a client, they can only see their own data
    useEffect(() => {
        if (isClient && user?.id) {
            setSelectedCustomerId(user.id);
        } else if (!isClient && allowAllOption && selectedCustomerId === undefined) {
            // Default to "All" for ledger and invoices
            setSelectedCustomerId(0);
        }
    }, [isClient, user, allowAllOption, selectedCustomerId]);

    const handleCustomerChange = (_: any, newValue: { id: number; name: string } | null) => {
        if (newValue) {
            setSelectedCustomerId(newValue.id);
        } else {
            setSelectedCustomerId(allowAllOption ? 0 : undefined);
            dispatch(clearFinanceLedger());
        }
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: FinanceTab) => {
        setActiveTab(newValue);
        // Update URL when tab changes
        navigate(tabToPathMap[newValue]);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Customer Finances
                </Typography>

                {!isClient && (
                    <Box sx={{ maxWidth: { xs: '100%', md: 400 }, mb: 2 }}>
                        <Autocomplete
                            options={customerOptions}
                            getOptionLabel={(option) => option.name}
                            loading={clientsLoading}
                            onChange={handleCustomerChange}
                            value={customerOptions.find(opt => opt.id === selectedCustomerId) || null}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Customer"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {clientsLoading ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>
                )}
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                >
                    <Tab label="Ledger" value="ledger" />
                    <Tab label="Payments" value="payments" />
                    {!isClient && <Tab label="New Payment" value="newPayment" />}
                    <Tab label="Debit / Credit Notes" value="notes" />
                    <Tab label="Invoices" value="invoices" />
                </Tabs>
            </Box>

            <Box sx={{ minHeight: 400 }}>
                {activeTab === 'ledger' && (
                    <LedgerTab selectedCustomerId={selectedCustomerId === 0 ? undefined : selectedCustomerId} isClient={!!isClient} />
                )}
                {activeTab === 'payments' && (
                    <PaymentsTab selectedCustomerId={selectedCustomerId === 0 ? undefined : selectedCustomerId} />
                )}
                {activeTab === 'newPayment' && !isClient && (
                    <PaymentCreateTab selectedCustomerId={selectedCustomerId === 0 ? undefined : selectedCustomerId} />
                )}
                {activeTab === 'notes' && (
                    <NotesTab
                        selectedCustomerId={selectedCustomerId === 0 ? undefined : selectedCustomerId}
                        userType={isClient ? 'CLIENT' : 'ADMIN'}
                        customerOptions={customerOptions}
                        isClient={!!isClient}
                    />
                )}
                {activeTab === 'invoices' && (
                    <InvoicesTab selectedCustomerId={selectedCustomerId === 0 ? undefined : selectedCustomerId} />
                )}
            </Box>
        </Container>
    );
};

export default CustomerFinances;
